import { useCallback, useEffect, useState } from 'react';
import { readUnhandledEnumCase } from 'rpc-client-utils';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { View } from 'foundations-components/components/View';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { reportErrorToLogFetch } from 'academy-common-lib/utils/errorUtils';
import ExamQuestion from './ExamQuestion';
import ExamTimer from './ExamTimer';
import QuizTopbar from '../../../components/QuizTopbar';
import UsageTracker from '../../../utils/UsageTracker';
import { QuizWrapper } from './QuizWrapper';
import { useSubmitExamQuestion, useValidateExam } from '../examsQueries';
import { BaseContentControlWrapper } from '../../../components/ContentControlWrapper';
import { ContactUsLink } from '../../../components/ContactUsLink';
import { Metrics } from '../../../Metrics';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ExamQuestionScreen = ({
  track,
  mostRecentExamAttempt,
  clientStartTime
}) => {
  const [selectedAnswerIds, setSelectedAnswerIds] = useState([]);
  const [submitExamQuestion, {
    submitExamQuestionLoading,
    submitExamQuestionError
  }] = useSubmitExamQuestion();
  const [validateExam, {
    validateExamLoading,
    validateExamError
  }] = useValidateExam(track.id, track.language);
  const totalQuestionCount = mostRecentExamAttempt ? mostRecentExamAttempt.questions.length : 0;
  const currentQuestionIndex = mostRecentExamAttempt ? Math.max(0, Math.min(mostRecentExamAttempt.questionSubmissions.length, totalQuestionCount - 1)) : 0;
  const currentQuestion = mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 ? void 0 : mostRecentExamAttempt.questions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex + 1 >= totalQuestionCount;
  const handleClickNext = useCallback(() => {
    if (!currentQuestion || !selectedAnswerIds.length) {
      return null;
    }
    return submitExamQuestion(track.id, mostRecentExamAttempt.id, currentQuestion.id, selectedAnswerIds).then(() => {
      var _track$exam2, _mostRecentExamAttemp;
      setSelectedAnswerIds([]);
      Metrics.counter('exam-question-answered', {
        language: readUnhandledEnumCase(mostRecentExamAttempt.language)
      }).increment();
      if (isLastQuestion) {
        return validateExam(mostRecentExamAttempt.id).then(() => {
          var _track$exam;
          UsageTracker.trackStandalone('Exam Interaction', {
            action: 'completed',
            examId: (_track$exam = track.exam) === null || _track$exam === void 0 ? void 0 : _track$exam.id,
            examLength: mostRecentExamAttempt.questions.length,
            trackId: track.id,
            trackName: track === null || track === void 0 ? void 0 : track.name,
            examAttemptId: mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 ? void 0 : mostRecentExamAttempt.id.toString(),
            language: readUnhandledEnumCase(mostRecentExamAttempt.language)
          });
          Metrics.counter('exam-completed', {
            language: readUnhandledEnumCase(mostRecentExamAttempt.language)
          }).increment();
        }).catch(error => {
          Metrics.counter('exam-mutation-call-error', {
            action: 'exam-questions-validation-error'
          }).increment();
          reportErrorToLogFetch({
            loggingTitle: 'Validate Exam Error',
            action: 'exam-questions-validation-error',
            error
          });
          FloatingAlertStore.addAlert({
            type: 'danger',
            message: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "learningCenter.exams.examQuestion.submitExamError",
              options: {
                contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
              }
            })
          });
        });
      }
      const completionPercentage = +((currentQuestionIndex + 1 / totalQuestionCount) * 100).toFixed(2);
      UsageTracker.trackStandalone('Exam Interaction', {
        action: 'progress',
        examId: (_track$exam2 = track.exam) === null || _track$exam2 === void 0 ? void 0 : _track$exam2.id,
        examLength: mostRecentExamAttempt.questions.length,
        trackId: track.id,
        trackName: track === null || track === void 0 ? void 0 : track.name,
        examAttemptId: mostRecentExamAttempt === null || mostRecentExamAttempt === void 0 || (_mostRecentExamAttemp = mostRecentExamAttempt.id) === null || _mostRecentExamAttemp === void 0 ? void 0 : _mostRecentExamAttemp.toString(),
        completionPercentage,
        language: readUnhandledEnumCase(mostRecentExamAttempt.language)
      });
      return null;
    }).catch(error => {
      Metrics.counter('exam-mutation-call-error', {
        action: 'exam-submit-question-error'
      }).increment();
      reportErrorToLogFetch({
        loggingTitle: 'Submit Exam Question Error',
        action: 'exam-submit-question-error',
        error
      });
      FloatingAlertStore.addAlert({
        type: 'danger',
        message: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "learningCenter.exams.examQuestion.submitAnswerError",
          options: {
            contactUsLink: /*#__PURE__*/_jsx(ContactUsLink, {})
          }
        })
      });
    });
  }, [currentQuestion, currentQuestionIndex, isLastQuestion, mostRecentExamAttempt, selectedAnswerIds, submitExamQuestion, totalQuestionCount, track, validateExam]);
  useEffect(() => {
    const handleKeydown = event => {
      if (event.key === 'Enter' && selectedAnswerIds.length && !(submitExamQuestionLoading || validateExamLoading)) {
        void handleClickNext();
      }
    };
    document.addEventListener('keydown', handleKeydown);
    return () => document.removeEventListener('keydown', handleKeydown);
  }, [currentQuestionIndex, handleClickNext, selectedAnswerIds.length, submitExamQuestionLoading, validateExamLoading]);
  if (!mostRecentExamAttempt || !currentQuestion) return null;
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsxs(QuizWrapper, {
      children: [/*#__PURE__*/_jsx(QuizTopbar, {
        currentQuestion: currentQuestionIndex + 1,
        numQuestions: totalQuestionCount,
        topRightComponent: /*#__PURE__*/_jsx(ExamTimer, {
          clientStartTime: clientStartTime,
          mostRecentExamAttempt: mostRecentExamAttempt,
          track: track
        })
      }), /*#__PURE__*/_jsxs(View, {
        inlineSize: "100%",
        paddingBlockStart: "500",
        children: [/*#__PURE__*/_jsx("h2", {
          children: currentQuestion.question
        }), /*#__PURE__*/_jsx(ExamQuestion, {
          mostRecentExamAttempt: mostRecentExamAttempt,
          onSelectAnswer: setSelectedAnswerIds,
          selectedAnswerIds: selectedAnswerIds
        })]
      })]
    }), /*#__PURE__*/_jsx(BaseContentControlWrapper, {
      borderStyle: "none",
      children: /*#__PURE__*/_jsx(LoadingButton, {
        "data-test-id": "exam-next-question-button",
        disabled: selectedAnswerIds.length <= 0,
        failed: !!submitExamQuestionError || !!validateExamError,
        loading: submitExamQuestionLoading || validateExamLoading,
        onClick: () => {
          void handleClickNext();
        },
        preventClicksOnLoading: submitExamQuestionLoading || validateExamLoading,
        use: "primary",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.exams.examQuestion.nextButton"
        })
      })
    })]
  });
};
export default ExamQuestionScreen;